import { useCallback, useEffect, useState } from 'react';
import { useFlags } from '@atlaskit/flag';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import TransportGroupsApiService from 'core/api/transport-groups/transport-groups-api.service';
import { IEligibleDriver, ITransportGroup } from 'core/api/transport-groups/transport-groups-api-interface';
import DriverPicker, { ANY_DRIVER } from './driver-picker';

/**
 * Naming a driver for a batch that already exists.
 *
 * Only offered for a batch nobody has been given: one that already has a driver
 * has been handed over, and reassigning from a row of buttons takes work out
 * from under somebody who may be on their way to it.
 *
 * The same control as the commit dialog uses, so the drivers are described
 * identically in both places — but here a name is required, because the batch
 * is already available to everybody and choosing that again changes nothing.
 */

interface IProps {
  group: ITransportGroup;
}

const AssignBatchDialog = ({ group }: IProps) => {
  /**
   * Whether "any available driver" is a change rather than a restatement.
   *
   * From a batch nobody has, it is the state it is already in, so submitting it
   * would do nothing. From one that has a driver, it is the whole point: it
   * takes the batch off them and puts it back where anybody can take it.
   */
  const poolIsAChange = group.status === 'assigned';

  const dialog = useDialog();
  const flags = useFlags();
  const [drivers, setDrivers] = useState<IEligibleDriver[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [driversError, setDriversError] = useState<string>();
  const [chosen, setChosen] = useState<string>(group.driver?.uid ?? ANY_DRIVER);
  const [busy, setBusy] = useState(false);

  /** Kept out of the effect so the retry button can call the same thing. */
  const loadDrivers = useCallback(() => {
    setLoadingDrivers(true);
    setDriversError(undefined);
    TransportGroupsApiService.drivers()
      .then(({ drivers: eligible }) => setDrivers(eligible))
      .catch((caught) => setDriversError((caught as Error).message))
      .finally(() => setLoadingDrivers(false));
  }, []);

  useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  const save = async () => {
    const driver = drivers.find((candidate) => candidate.uid === chosen);
    if (!driver && !poolIsAChange) return;

    setBusy(true);
    try {
      if (driver) {
        await TransportGroupsApiService.assign(group.uid, { uid: driver.uid, fullName: driver.fullName });
        showSuccessFlag('Batch assigned', `${group.reference} (${group.label}) has been assigned to ${driver.fullName}.`, flags);
      } else {
        await TransportGroupsApiService.post(group.uid);
        showSuccessFlag('Batch available', `${group.reference} (${group.label}) is available for any driver to accept.`, flags);
      }
      dialog?.closeDialog();
    } catch (caught) {
      showErrorFlag('That did not work', (caught as Error).message, flags);
      setBusy(false);
    }
  };

  return (
    <div className='flex w-full flex-col rounded-lg bg-white lg:w-[560px]' data-testid='assign-batch-dialog'>
      <div className='border-b p-4'>
        <p className='headline-05 font-semibold'>{poolIsAChange ? 'Re-assign this batch' : 'Assign this batch'}</p>
      </div>

      <div className='max-h-[60vh] overflow-y-auto p-4'>
        <p className='body-02 mb-4'>{group.label}. Choose who takes it, or leave it for any driver to accept.</p>
        <DriverPicker
          drivers={drivers}
          loading={loadingDrivers}
          error={driversError}
          onRetry={loadDrivers}
          value={chosen}
          onChange={setChosen}
        />
      </div>

      <div className='flex justify-end gap-2 border-t p-4'>
        <SharedButton type='button' appearance='subtle' label='Cancel' onClick={() => dialog?.closeDialog()} />
        <SharedButton
          type='button'
          appearance='primary'
          label={poolIsAChange && chosen === ANY_DRIVER ? 'Put back to the pool' : 'Assign'}
          testId='confirm-assign'
          isLoading={busy}
          // Nothing to confirm until somebody is actually named. This dialog is
          // only reachable from a batch already available to every driver, so
          // "any available driver" is the state it is in rather than a change —
          // submitting it would be a button that appears to do something and
          // does nothing.
          disabled={loadingDrivers || (chosen === ANY_DRIVER && !poolIsAChange)}
          onClick={save}
        />
      </div>
    </div>
  );
};

export default AssignBatchDialog;
